import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "~/components/ui/button";
import { m } from "~/lib/i18n";

/* The missing page rendered as a delisted quote: a trend line that cliffs to
   zero and flatlines into a dead last tick. Paths carry pathLength={1} so the
   CSS draw-in in app.css stays geometry-independent. */
function FlatlineChart() {
  return (
    <svg
      aria-hidden="true"
      className="w-full"
      fill="none"
      viewBox="0 0 384 120"
    >
      <g className="text-border" stroke="currentColor" strokeWidth="1">
        <line x1="0" x2="384" y1="24" y2="24" />
        <line x1="0" x2="384" y1="64" y2="64" />
        <line strokeDasharray="3 5" x1="0" x2="384" y1="104" y2="104" />
      </g>
      <path
        className="error-page-trend text-muted-foreground"
        d="M0 30 L16 24 L32 40 L48 30 L64 46 L78 38 L96 56 L110 44 L128 62 L142 52 L158 70 L174 58 L190 78 L204 68 L220 86 L236 76"
        pathLength={1}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        className="error-page-flatline text-status-negative-foreground"
        d="M236 76 L246 104 L340 104"
        pathLength={1}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <g className="error-page-dot text-status-negative-foreground">
        <circle
          cx="340"
          cy="104"
          opacity="0.25"
          r="7"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle cx="340" cy="104" fill="currentColor" r="3" />
      </g>
    </svg>
  );
}

export function NotFound({ children }: { children?: ReactNode }) {
  return (
    <div className="flex min-h-[calc(100dvh-4rem)] w-full flex-1 items-center justify-center px-6 py-16">
      <div className="flex w-full max-w-sm flex-col items-center text-center">
        <FlatlineChart />
        <div
          aria-hidden="true"
          className="error-page-rise mt-7 flex items-baseline gap-3 [animation-delay:60ms]"
        >
          <span className="text-[2.5rem] leading-none font-medium tracking-tight tabular-nums">
            $0.0
            <span className="relative top-[0.1em] text-[0.7em] text-muted-foreground">
              4
            </span>
            04
          </span>
        </div>
        <h1 className="error-page-rise text-title-sm mt-5 font-medium tracking-tight [animation-delay:120ms]">
          {m.components_not_found_title()}
        </h1>
        <p className="error-page-rise text-meta mt-1.5 text-balance text-muted-foreground [animation-delay:180ms]">
          {children ?? m.components_not_found_default_description()}
        </p>
        <div className="error-page-rise mt-7 flex flex-wrap justify-center gap-2 [animation-delay:240ms]">
          <Button
            onClick={() => window.history.back()}
            type="button"
            variant="outline"
          >
            {m.components_not_found_go_back_label()}
          </Button>
          <Link className="inline-flex" to="/">
            <Button render={<span />}>
              {m.components_not_found_go_home_label()}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
